import { UnClickIcon } from '@carboncopyco/icons';
import type {
  BlockInteractionsRule,
  ILinkReplaceRule,
} from '@carboncopyco/types';
import { RuleApplyOnType } from '@carboncopyco/types';
import {
  Button,
  InputText,
  MessageBox,
  PositionedToggle,
  TextCaption,
} from '@carboncopyco/ui';
import { validateUrl } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ApplyOnDropdown } from '../ApplyOnDropdown';
import { InputField } from '../InputField';
import { FindByDropDown } from './FindByDropDown';
import { getSelectorTypeToValue, isValidExternalUrl } from './utils';

export interface FindAndReplaceLinkFormProps {
  initialValues?: Partial<ILinkReplaceRule>;
  onSubmit: (
    values: Partial<ILinkReplaceRule> & {
      pageUrl: string;
    }
  ) => Promise<void>;
  submitText?: string;
  onCancel?: () => void;
  disabled?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  elementType?: ILinkReplaceRule['replaceType'];
  internalLinkFindAndReplace?: boolean;
  elementMetadata?: Partial<HTMLButtonElement & HTMLAnchorElement>;
  currentPageUrl?: string;
  blockInteractionModification?: BlockInteractionsRule[];
}

export const FindAndReplaceLinkForm = ({
  onSubmit,
  onCancel,
  initialValues,
  submitText,
  internalLinkFindAndReplace,
  elementType,
  elementMetadata,
  currentPageUrl,
  onFocus,
  blockInteractionModification,
}: FindAndReplaceLinkFormProps) => {
  const [newValue, setNewValue] = useState(initialValues?.newValue ?? '');
  const [applyOn, setApplyOn] = useState<RuleApplyOnType>(
    initialValues ? initialValues.applyOn : RuleApplyOnType.allPages
  );
  const [isNewReplaceHasBlockInteraction, { set: setIsNewReplaceBlocked }] =
    useBoolean(false);
  const [isOldReplaceHasBlockInteraction, { set: setIsOldReplaceBlocked }] =
    useBoolean(false);
  const [isInternalLink, { toggle: toggleIsInternalLink }] = useBoolean(
    initialValues ? !initialValues.isExternal : false
  );
  const [oldValue, setOldValue] = useState<string>(
    initialValues?.oldValue ?? ''
  );
  const pageUrl = useMemo(() => {
    if (initialValues && initialValues.applyOn === applyOn) {
      return initialValues?.elementData?.pageUrl || '';
    }
    if (applyOn === RuleApplyOnType.currentPage) {
      return currentPageUrl;
    }
    return '';
  }, [initialValues, currentPageUrl, applyOn]) as string;

  const [findBy, setFindBy] = useState<ILinkReplaceRule['findBy']>(
    initialValues
      ? initialValues.findBy
      : elementType === 'button'
        ? 'title'
        : 'url'
  );

  useEffect(() => {
    if (!initialValues?.newValue) {
      setOldValue(getSelectorTypeToValue(elementMetadata)[findBy]);
    }
  }, [initialValues, findBy]);

  const isLinkHasBlockInteraction = (replaceValue: string) =>
    blockInteractionModification?.some(
      ({ blockingValue }) => blockingValue === replaceValue
    );

  useEffect(() => {
    if (!oldValue) return;
    setIsOldReplaceBlocked(isLinkHasBlockInteraction(oldValue));
  }, [oldValue]);

  const submitButtonText = useMemo(() => {
    if (isNewReplaceHasBlockInteraction) return 'Add rule anyway';
    return submitText ?? 'Add rule';
  }, [isNewReplaceHasBlockInteraction]);

  const replaceUrlErrorMsg = useMemo(() => {
    if (!newValue?.length) return '';
    // if internal link validate if there are no illegal characters
    const urlValidation = isInternalLink
      ? validateUrl(newValue)
      : isValidExternalUrl(newValue);

    if (!urlValidation) {
      return 'Invalid link';
    }

    return '';
  }, [newValue, isInternalLink]);

  const handleSubmit = async () => {
    const isLinkHasBeenBlocked =
      !isNewReplaceHasBlockInteraction && isLinkHasBlockInteraction(newValue);
    if (isLinkHasBeenBlocked) {
      setIsNewReplaceBlocked(true);
      return;
    }
    return await onSubmit({
      newValue,
      applyOn,
      findBy,
      pageUrl,
      isExternal: !isInternalLink,
    });
  };

  const shouldDisableSubmit = useMemo(() => {
    return (
      !newValue?.length ||
      (initialValues?.newValue === newValue &&
        applyOn === initialValues.applyOn) ||
      replaceUrlErrorMsg.length > 0
    );
  }, [newValue, replaceUrlErrorMsg, applyOn, initialValues]);

  if (!elementMetadata && !initialValues) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-4"
      aria-label="Find and replace link"
    >
      <div className="flex flex-col gap-3.5">
        <FindByDropDown
          findBy={findBy}
          setFindBy={setFindBy}
          elementMetadata={elementMetadata}
          elementType={elementType ?? initialValues?.replaceType}
          initialOldValue={initialValues?.oldValue}
        />
        {isOldReplaceHasBlockInteraction && (
          <MessageBox
            leftIcon={<UnClickIcon />}
            label="Link is blocked by block-interaction rule, changing it will restore access."
          />
        )}
        <div className="flex flex-col">
          <InputField
            label="Replace URL with"
            name="Replace URL with"
            error={replaceUrlErrorMsg}
          >
            <div className="flex flex-col gap-1.5">
              <InputText
                multiline
                multilineOptions={{
                  isExpandableHeight: true,
                }}
                autoFocus
                size="md"
                value={newValue as string}
                onFocus={onFocus}
                onChange={({ target }) => setNewValue(target.value)}
                dataDsId="replace-url"
              />
              {!internalLinkFindAndReplace && !replaceUrlErrorMsg && (
                <TextCaption label="The link will open in a new tab." />
              )}
            </div>
          </InputField>
          {internalLinkFindAndReplace && (
            <div className="flex flex-col mt-4 gap-0.5">
              <div className="flex justify-between">
                <TextCaption
                  label="Use internal link"
                  color="text-ds-natural-600"
                />
                <PositionedToggle
                  disabled={!!initialValues}
                  name="isInternal"
                  checked={isInternalLink}
                  onChange={toggleIsInternalLink}
                />
              </div>
              <TextCaption label="Switch the current page to a different page within the demo." />
            </div>
          )}
        </div>
      </div>
      <ApplyOnDropdown
        applyOn={applyOn}
        setApplyOn={setApplyOn}
        currentPageUrl={pageUrl}
      />
      {isNewReplaceHasBlockInteraction && (
        <MessageBox
          label="The applied URL is identified as blocked due to a block-rule. This edit will restore access to the link. "
          leftIcon={<UnClickIcon />}
        />
      )}
      <div className="flex justify-end">
        <div className="flex flex-row gap-2.5">
          <Button
            label="Cancel"
            onClick={onCancel}
            highContrast
          />
          <Button
            aria-label="Add rule"
            label={submitButtonText}
            onClick={handleSubmit}
            disabled={shouldDisableSubmit}
          />
        </div>
      </div>
    </div>
  );
};
